/******************************************************************************
 * @Author                : Hamza Damene <hamzadamene@chronolink.com>         *
 * @CreatedDate           : 2025-01-22 18:36:16                               *
 * @LastEditors           : Hamza Damene <hamzadamene@chronolink.com>         *
 * @LastEditDate          : 2026-02-17 12:00:00                               *
 *****************************************************************************/

/* SUMMARY
  * downloadXlsx: download data as XLSX file
*/

import { t } from "i18next";
import * as XLSX from "xlsx";

/**
* downloadXlsx: download data as XLSX file
* @param filename - file name
* @param data - data to convert
* @param descriptor - mapping descriptor
*/
export const downloadXlsx = <T>(
  filename: string,
  data: T | T[],
  descriptor: EasyTech.ExportDescriptor[]
) => {
  // Ensure data is an array
  const dataArray = Array.isArray(data) ? data : [data];

  // Map data using descriptor
  const formattedData = dataArray.map((item) => {
    const row: any = {};
    descriptor.forEach((desc) => {
      // Use translation if columnTranslationKey is provided, otherwise use column directly
      const header = desc.columnTranslationKey ? t(desc.columnTranslationKey) : desc.column;
      row[header] = desc.getter(item);
    });
    return row;
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Ensure correct extension
  const extension = ".xlsx";
  const safeFilename = filename.endsWith(extension)
    ? filename
    : (filename.endsWith(".csv") ? filename.replace(".csv", extension) : filename + extension);

  // Download file
  XLSX.writeFile(workbook, safeFilename);
};

/** One sheet of a multi-sheet workbook. */
export interface XlsxSheet<T> {
  /** Sheet name. Sanitised before use: Excel caps it at 31 chars and rejects []:*?/\ */
  name: string;
  data: T[];
  descriptor: EasyTech.ExportDescriptor[];
}

/** Excel rejects these in a sheet name, and silently truncates past 31 characters. */
const sanitizeSheetName = (name: string): string =>
  name.replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 31) || "Sheet";

/**
* downloadXlsxMultiSheet: download several descriptor-mapped tables as one workbook.
*
* A separate function rather than a wider signature on downloadXlsx: that one has seven call sites
* across the list views, and none of them want a second parameter they will never pass.
*
* Unlike downloadXlsx, a `.csv` filename is honoured rather than rewritten — but CSV holds a single
* table, so only the first sheet is written and the caller is responsible for passing one.
*
* @param filename - file name, with or without extension
* @param sheets - one entry per table
*/
export const downloadXlsxMultiSheet = (filename: string, sheets: XlsxSheet<any>[]) => {
  const workbook = XLSX.utils.book_new();
  const used = new Set<string>();

  sheets.forEach((sheet, index) => {
    const rows = sheet.data.map((item) => {
      const row: any = {};
      sheet.descriptor.forEach((desc) => {
        const header = desc.columnTranslationKey ? t(desc.columnTranslationKey) : desc.column;
        row[header] = desc.getter(item);
      });
      return row;
    });

    // Two blocks can legitimately translate to the same name; Excel refuses duplicates outright.
    let name = sanitizeSheetName(sheet.name);
    while (used.has(name)) {
      name = sanitizeSheetName(`${name.slice(0, 28)} ${index + 1}`);
    }
    used.add(name);

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), name);
  });

  const isCsv = filename.endsWith(".csv");
  const safeFilename = isCsv || filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;

  XLSX.writeFile(workbook, safeFilename);
};
